import { Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader, SectionHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import { MovementsTable } from "@/features/staff/inventory/movements-table"
import { StockAdjustmentDialog } from "@/features/staff/inventory/stock-adjustment-dialog"
import {
  INVENTORY_PATHS,
  supplyAlert,
  tInventory,
  type AdjustmentReasonOption,
  type ShortageState,
  type SupplyDetail,
} from "@/features/staff/inventory/inventory"
import { t } from "@/lib/i18n"

export type SupplyLedgerProps = {
  supply: SupplyDetail
  adjustmentReasons: AdjustmentReasonOption[]
  shortage: ShortageState
  timeZone: string | null
}

const PAGE_SCOPE = "ui.react.pages.staff.location.manage.supplies.show"

/**
 * One supply's history, and the one place a human may add to it.
 *
 * ## Read-only by construction, not by omission
 *
 * There is no edit control and no delete control on any row, and there is no
 * endpoint behind one either: `Location::StockMovement` is `readonly?` once
 * persisted and the routes offer `create` alone. A ledger whose rows can be
 * revised is not a ledger, and the reason to keep one is precisely that somebody
 * will eventually ask what happened to the tequila. The sentence under the
 * section header says so out loud, because an operator who cannot find the pencil
 * will otherwise assume the screen is unfinished rather than deliberate.
 *
 * The answer to a row written wrong is a count correction, which is a **new row
 * in the opposite direction** — and both stay visible, which is the part that
 * matters when the question is whether stock walked out or was miscounted.
 *
 * ## The alert here is a reading, not a claim
 *
 * The badge beside the balance is gated on `shortage.reportable` exactly as the
 * list screen is. A restaurant with no recipes has nothing drawing stock down,
 * so calling a balance "short" would be asserting a shortage that the mechanism
 * to produce cannot exist yet.
 */
export function SupplyLedger({
  supply,
  adjustmentReasons,
  shortage,
  timeZone,
}: SupplyLedgerProps) {
  const alert = shortage.reportable ? supplyAlert(supply) : null

  return (
    <MainContainer size="md">
      <div className="space-y-6">
        <PageHeader
          title={supply.name}
          description={t(`${PAGE_SCOPE}.description`)}
          actions={
            <>
              <Button asChild variant="outline" data-testid="supply-ledger-back">
                <Link href={INVENTORY_PATHS.supplies}>
                  <ArrowLeft className="size-4" />
                  {tInventory("back")}
                </Link>
              </Button>
              <StockAdjustmentDialog supply={supply} reasons={adjustmentReasons} />
            </>
          }
        />

        <Card density="compact" data-testid="supply-summary">
          <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs">{tInventory("stock_label")}</p>
              <p className="flex items-center gap-2">
                <span
                  className="text-foreground text-sm font-semibold tabular-nums"
                  data-testid="supply-summary-stock"
                >
                  {supply.stockOnHand} {supply.unitLabel}
                </span>
                {alert && (
                  <StatusBadge
                    tone={alert === "short" ? "danger" : "warning"}
                    label={alert === "short" ? tInventory("short_badge") : tInventory("low_badge")}
                    data-testid="supply-summary-alert"
                  />
                )}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-muted-foreground text-xs">
                {tInventory("reorder_point_label")}
              </p>
              <p
                className="text-foreground text-sm font-semibold tabular-nums"
                data-testid="supply-summary-reorder-point"
              >
                {supply.reorderPoint
                  ? `${supply.reorderPoint} ${supply.unitLabel}`
                  : tInventory("reorder_point_placeholder")}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-muted-foreground text-xs">{tInventory("unit_label")}</p>
              <p className="text-foreground text-sm font-semibold">{supply.unitLabel}</p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <SectionHeader title={tInventory("ledger_title")} className="px-0" />
          <p className="text-muted-foreground text-sm">{tInventory("ledger_description")}</p>
          <p className="text-muted-foreground text-xs" data-testid="supply-ledger-immutable">
            {tInventory("ledger_immutable")}
          </p>

          {supply.movements.length === 0 ? (
            <Card data-testid="supply-ledger-empty">
              <CardContent className="py-10 text-center">
                <p className="text-muted-foreground text-sm">{tInventory("ledger_empty")}</p>
              </CardContent>
            </Card>
          ) : (
            <Card density="compact">
              <CardContent>
                <MovementsTable
                  movements={supply.movements}
                  unitLabel={supply.unitLabel}
                  timeZone={timeZone}
                  data-testid="supply-ledger"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MainContainer>
  )
}
